// SPDX-License-Identifier: Apache-2.0

// kHopBFS — TypeScript dispatch wrapper for level-synchronous BFS.
// One kernel invocation per BFS level; the host loop reads back the frontier
// counter between levels to know how many threads to dispatch next.
//
// CPU-vs-GPU breakeven: per the source-comment at vault-graph-queries.ts:4–7,
// CPU `kHopNeighbors` wins for graphs <10k nodes — the upload + per-level submit
// overhead dominates. Use this kernel only past ~50k nodes or for multi-source
// BFS. The /search route's UI surfaces this guidance to users.
//
// Diagnostic discipline (matches search.ts pattern):
//   - getCompilationInfo() after createShaderModule, throw on errors
//   - pushErrorScope("validation") around encoder + submit
//   - 0xffffffff sentinel for "unvisited" in hop_of
//   - throw if BFS fails to expand at all (no level-0 visit recorded)

import shaderSrc from "./kHopBFS.wgsl?raw";

export interface KHopBFSSetup {
  device: GPUDevice;
  pipeline: GPUComputePipeline;
  bindGroupLayout: GPUBindGroupLayout;

  paramsBuf: GPUBuffer;

  rowOffsetsBuf: GPUBuffer;
  colIndicesBuf: GPUBuffer;

  // Ping-pong frontier buffers
  frontierA: GPUBuffer;
  frontierB: GPUBuffer;

  visitedBuf: GPUBuffer;
  hopOfBuf: GPUBuffer;

  // Readback for frontier counter (small, 4 bytes)
  frontierCountReadback: GPUBuffer;
  // Readback for hop_of (full N×4 bytes)
  hopOfReadback: GPUBuffer;

  n: number;
  nEdges: number;
  visitedWords: number;
}

export interface KHopBFSResult {
  /** Visited node ids in BFS-discovery order, including the source at index 0. */
  visited: Uint32Array;
  /** Per-node hop count; 0xffffffff for nodes never reached. */
  hopOf: Uint32Array;
  /** Frontier sizes per level, levels[0]=1 (source), levels[k]=last level discovered. */
  levels: number[];
  /** Wall time from encoder start through final readback unmap. */
  totalMs: number;
}

const WG = 64;

export async function setupKHopBFS(
  device: GPUDevice,
  n: number,
  edges: Uint32Array, // flat list of [src0, dst0, src1, dst1, ...] for synthetic build, OR pre-built CSR
  options?: { csr?: { rowOffsets: Uint32Array; colIndices: Uint32Array } },
): Promise<KHopBFSSetup> {
  if (n < 1) throw new Error("n must be >= 1");

  let rowOffsets: Uint32Array;
  let colIndices: Uint32Array;
  if (options?.csr) {
    rowOffsets = options.csr.rowOffsets;
    colIndices = options.csr.colIndices;
    if (rowOffsets.length !== n + 1) {
      throw new Error(`csr.rowOffsets length ${rowOffsets.length} must equal n+1 = ${n + 1}`);
    }
  } else {
    // Build CSR from a flat [src, dst] edge list. Edges treated as directed;
    // caller should pass both directions for undirected graphs.
    if (edges.length % 2 !== 0) throw new Error("edges length must be even (pairs of u32)");
    const built = buildCSRFromEdges(n, edges);
    rowOffsets = built.rowOffsets;
    colIndices = built.colIndices;
  }
  const nEdges = colIndices.length;

  const module = device.createShaderModule({ code: shaderSrc, label: "kHopBFS" });
  await assertNoCompileErrors(module, "kHopBFS");

  const bindGroupLayout = device.createBindGroupLayout({
    label: "kHopBFS-bgl",
    entries: [
      { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
      { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } }, // frontier_out (atomic)
      { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } }, // visited (atomic)
      { binding: 6, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } }, // hop_of
    ],
  });
  const pipeline = device.createComputePipeline({
    label: "kHopBFS",
    layout: device.createPipelineLayout({ bindGroupLayouts: [bindGroupLayout] }),
    compute: { module, entryPoint: "main" },
  });

  const paramsBuf = device.createBuffer({
    label: "kHopBFS-params",
    size: 16,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  const rowOffsetsBuf = device.createBuffer({
    label: "kHopBFS-row-offsets",
    size: (n + 1) * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });
  const colIndicesBuf = device.createBuffer({
    label: "kHopBFS-col-indices",
    size: Math.max(4, nEdges * 4),
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(rowOffsetsBuf, 0, rowOffsets.buffer, rowOffsets.byteOffset, rowOffsets.byteLength);
  if (nEdges > 0) {
    device.queue.writeBuffer(colIndicesBuf, 0, colIndices.buffer, colIndices.byteOffset, colIndices.byteLength);
  }

  // Frontier buffers: [count, ids...]. Sized to N + 1 to hold worst-case.
  const frontierBytes = (n + 1) * 4;
  const frontierA = device.createBuffer({
    label: "kHopBFS-frontier-a",
    size: frontierBytes,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
  });
  const frontierB = device.createBuffer({
    label: "kHopBFS-frontier-b",
    size: frontierBytes,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
  });

  const visitedWords = Math.ceil(n / 32);
  const visitedBuf = device.createBuffer({
    label: "kHopBFS-visited",
    size: visitedWords * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
  });
  const hopOfBuf = device.createBuffer({
    label: "kHopBFS-hop-of",
    size: n * 4,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
  });

  const frontierCountReadback = device.createBuffer({
    label: "kHopBFS-count-readback",
    size: 4,
    usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
  });
  const hopOfReadback = device.createBuffer({
    label: "kHopBFS-hop-of-readback",
    size: n * 4,
    usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
  });

  return {
    device,
    pipeline,
    bindGroupLayout,
    paramsBuf,
    rowOffsetsBuf,
    colIndicesBuf,
    frontierA,
    frontierB,
    visitedBuf,
    hopOfBuf,
    frontierCountReadback,
    hopOfReadback,
    n,
    nEdges,
    visitedWords,
  };
}

export async function runKHopBFS(
  setup: KHopBFSSetup,
  sourceNode: number,
  k: number,
): Promise<KHopBFSResult> {
  if (sourceNode < 0 || sourceNode >= setup.n) {
    throw new Error(`sourceNode ${sourceNode} out of range [0, ${setup.n})`);
  }
  if (k < 0) throw new Error("k must be >= 0");

  const { device } = setup;
  const t0 = performance.now();

  // ---- init ----
  // Clear visited bitmap, set hop_of to 0xffffffff sentinel.
  // Seed visited bit + hop_of[source] = 0, frontierA = [1, source].
  device.pushErrorScope("validation");

  const initEncoder = device.createCommandEncoder({ label: "kHopBFS-init" });
  initEncoder.clearBuffer(setup.visitedBuf, 0, setup.visitedWords * 4);
  initEncoder.clearBuffer(setup.frontierB, 0, (setup.n + 1) * 4);
  device.queue.submit([initEncoder.finish()]);

  // hop_of = 0xffffffff for all, then overwrite source = 0.
  const hopOfInit = new Uint32Array(setup.n).fill(0xffffffff);
  hopOfInit[sourceNode] = 0;
  device.queue.writeBuffer(setup.hopOfBuf, 0, hopOfInit.buffer, hopOfInit.byteOffset, hopOfInit.byteLength);

  // Seed visited bitmap with the source bit.
  const visitedInit = new Uint32Array(setup.visitedWords);
  const word = sourceNode >>> 5;
  const bit = 1 << (sourceNode & 31);
  visitedInit[word] = bit;
  device.queue.writeBuffer(setup.visitedBuf, 0, visitedInit.buffer);

  // Seed frontierA with [1, source].
  const seed = new Uint32Array(2);
  seed[0] = 1;
  seed[1] = sourceNode;
  device.queue.writeBuffer(setup.frontierA, 0, seed.buffer);

  // ---- BFS levels ----
  const levels: number[] = [1]; // level 0 = source
  let inBuf: GPUBuffer = setup.frontierA;
  let outBuf: GPUBuffer = setup.frontierB;
  let frontierLen = 1;

  for (let level = 1; level <= k && frontierLen > 0; level++) {
    // Reset out frontier counter (and we don't bother clearing the rest).
    const zero = new Uint32Array([0]);
    device.queue.writeBuffer(outBuf, 0, zero.buffer);

    const params = new Uint32Array([level, frontierLen, setup.n, 0]);
    device.queue.writeBuffer(setup.paramsBuf, 0, params.buffer);

    const bindGroup = device.createBindGroup({
      layout: setup.bindGroupLayout,
      entries: [
        { binding: 0, resource: { buffer: setup.paramsBuf } },
        { binding: 1, resource: { buffer: setup.rowOffsetsBuf } },
        { binding: 2, resource: { buffer: setup.colIndicesBuf } },
        { binding: 3, resource: { buffer: inBuf } },
        { binding: 4, resource: { buffer: outBuf } },
        { binding: 5, resource: { buffer: setup.visitedBuf } },
        { binding: 6, resource: { buffer: setup.hopOfBuf } },
      ],
    });

    const encoder = device.createCommandEncoder({ label: `kHopBFS-level-${level}` });
    const pass = encoder.beginComputePass({ label: `kHopBFS-pass-${level}` });
    pass.setPipeline(setup.pipeline);
    pass.setBindGroup(0, bindGroup);
    pass.dispatchWorkgroups(Math.max(1, Math.ceil(frontierLen / WG)));
    pass.end();
    encoder.copyBufferToBuffer(outBuf, 0, setup.frontierCountReadback, 0, 4);
    device.queue.submit([encoder.finish()]);

    await setup.frontierCountReadback.mapAsync(GPUMapMode.READ);
    const countView = new Uint32Array(setup.frontierCountReadback.getMappedRange().slice(0));
    setup.frontierCountReadback.unmap();
    frontierLen = countView[0] ?? 0;
    levels.push(frontierLen);

    // Swap.
    const tmp = inBuf;
    inBuf = outBuf;
    outBuf = tmp;
  }

  // Readback hop_of fully.
  const finalEncoder = device.createCommandEncoder({ label: "kHopBFS-final-readback" });
  finalEncoder.copyBufferToBuffer(setup.hopOfBuf, 0, setup.hopOfReadback, 0, setup.n * 4);
  device.queue.submit([finalEncoder.finish()]);

  const validationError = await device.popErrorScope();
  if (validationError) {
    throw new Error(`WebGPU validation error during kHopBFS: ${validationError.message}`);
  }

  await setup.hopOfReadback.mapAsync(GPUMapMode.READ);
  const hopOf = new Uint32Array(setup.hopOfReadback.getMappedRange().slice(0));
  setup.hopOfReadback.unmap();

  // Build the visited list from hop_of: any node with hop != 0xffffffff is visited.
  // Order: ascending hop, then ascending node id (stable, host-side).
  const visitedPairs: { id: number; hop: number }[] = [];
  for (let i = 0; i < setup.n; i++) {
    const h = hopOf[i] ?? 0xffffffff;
    if (h !== 0xffffffff) visitedPairs.push({ id: i, hop: h });
  }
  visitedPairs.sort((a, b) => a.hop - b.hop || a.id - b.id);
  const visited = new Uint32Array(visitedPairs.length);
  for (let i = 0; i < visitedPairs.length; i++) {
    visited[i] = visitedPairs[i]?.id ?? 0;
  }

  const t1 = performance.now();

  if (visited.length === 0) {
    throw new Error(
      "kHopBFS produced zero visited nodes — even the source should be present. " +
      "Check that hop_of init writeBuffer landed before the first dispatch.",
    );
  }

  return { visited, hopOf, levels, totalMs: t1 - t0 };
}

export function disposeKHopBFS(setup: KHopBFSSetup): void {
  setup.paramsBuf.destroy();
  setup.rowOffsetsBuf.destroy();
  setup.colIndicesBuf.destroy();
  setup.frontierA.destroy();
  setup.frontierB.destroy();
  setup.visitedBuf.destroy();
  setup.hopOfBuf.destroy();
  setup.frontierCountReadback.destroy();
  setup.hopOfReadback.destroy();
}

async function assertNoCompileErrors(module: GPUShaderModule, label: string): Promise<void> {
  const info = await module.getCompilationInfo();
  const errors = info.messages.filter((m) => m.type === "error");
  if (errors.length > 0) {
    const text = errors.map((e) => `  L${e.lineNum}:${e.linePos} ${e.message}`).join("\n");
    throw new Error(`${label} WGSL compile errors:\n${text}`);
  }
  const warnings = info.messages.filter((m) => m.type === "warning");
  if (warnings.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(`[${label}] compile warnings:`, warnings);
  }
}

/**
 * Build CSR from a flat [src, dst, src, dst, ...] edge list.
 * Sorts edges by src (counting sort) to produce row_offsets + col_indices.
 */
function buildCSRFromEdges(
  n: number,
  edges: Uint32Array,
): { rowOffsets: Uint32Array; colIndices: Uint32Array } {
  const m = edges.length / 2;
  const degree = new Uint32Array(n);
  for (let i = 0; i < m; i++) {
    const src = edges[i * 2] ?? 0;
    if (src < n) degree[src] = (degree[src] ?? 0) + 1;
  }
  const rowOffsets = new Uint32Array(n + 1);
  let cum = 0;
  for (let i = 0; i < n; i++) {
    rowOffsets[i] = cum;
    cum += degree[i] ?? 0;
  }
  rowOffsets[n] = cum;

  const colIndices = new Uint32Array(cum);
  const cursor = new Uint32Array(n);
  for (let i = 0; i < m; i++) {
    const src = edges[i * 2] ?? 0;
    const dst = edges[i * 2 + 1] ?? 0;
    if (src < n) {
      const off = (rowOffsets[src] ?? 0) + (cursor[src] ?? 0);
      colIndices[off] = dst;
      cursor[src] = (cursor[src] ?? 0) + 1;
    }
  }
  return { rowOffsets, colIndices };
}

/**
 * Build a synthetic random undirected graph with average degree `avgDegree`
 * (so total directed edges ≈ n * avgDegree). Returns a flat [src, dst, ...]
 * edge list with both directions present.
 *
 * The graph is connected enough that BFS from node 0 with k=2 produces
 * nontrivial level counts at any reasonable n (degree ≥ 4).
 */
export function buildRandomGraph(n: number, avgDegree: number, seed = 1): Uint32Array {
  if (n < 2) throw new Error("n must be >= 2");
  if (avgDegree < 1) throw new Error("avgDegree must be >= 1");
  let s = seed >>> 0;
  const rand = (): number => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const halfEdges = Math.floor((n * avgDegree) / 2);
  const out = new Uint32Array(halfEdges * 4); // each undirected edge → 2 directed entries × 2 u32s each
  let cursor = 0;
  for (let i = 0; i < halfEdges; i++) {
    const a = Math.floor(rand() * n);
    let b = Math.floor(rand() * n);
    if (a === b) b = (b + 1) % n; // avoid self-loops
    out[cursor++] = a;
    out[cursor++] = b;
    out[cursor++] = b;
    out[cursor++] = a;
  }
  return out;
}
